import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StyledDatePicker, StyledTextField } from 'components/molecules/FormDatePicker/styles';
import { isValidDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { DateView } from '@mui/x-date-pickers';
import FormFooterLabel from '../../atoms/FormFooterLabel';

export enum EFormInputTypes {
  text = 'text',
  password = 'password',
}

interface IFormInput extends IFormElementProps {
  placeholder?: string;
  footerLabel?: string;
  minDate?: Date;
  openTo?: DateView;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  dateFormat?: string;
  disabled?: boolean;
  isClearable?: boolean;
}

export const FormDatePicker = ({
  name,
  label,
  footerLabel,
  control,
  errors,
  minDate,
  openTo = 'day',
  arrayName,
  arrayIndex,
  fieldName,
  placeholder,
  dateFormat,
  disabled = false,
  isClearable = false,
}: IFormInput) => {
  const { shortDateFormat, getDateFormat } = useGlobalProperty();

  useEffect(() => {
    if (!shortDateFormat) {
      getDateFormat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error =
    arrayName && arrayIndex !== undefined && fieldName
      ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
      : errors[name]?.message;

  const finalDateFormat = dateFormat || shortDateFormat;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledFormElementWrapper disabled={disabled}>
        <FormLabel name={name} label={label} disabled={disabled} />
        <Controller
          name={name}
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <StyledDatePicker
              openTo={openTo}
              componentsProps={{
                actionBar: {
                  actions: ['clear'],
                },
              }}
              onAccept={(newDate) => {
                if (newDate === null) {
                  field.onChange(null);
                }
              }}
              disabled={disabled}
              format={finalDateFormat}
              value={field.value !== '' && !!field.value ? new Date(field.value) : null}
              minDate={minDate}
              onChange={(e) => {
                if (isValidDate(e)) {
                  field.onChange(e);
                } else {
                  field.onChange('');
                }
              }}
              slots={{
                textField: StyledTextField,
              }}
              slotProps={{
                field: {
                  clearable: isClearable,
                },
                textField: {
                  ref: field.ref,
                  inputProps: {
                    inputMode: 'numeric',
                    placeholder: placeholder || finalDateFormat.toUpperCase(),
                    id: name,
                    disabled: !!disabled,
                  },
                  error: !!error,
                },
              }}
            />
          )}
        />
        {footerLabel && <FormFooterLabel label={footerLabel} />}
        <FormErrorLabel label={error} />
      </StyledFormElementWrapper>
    </LocalizationProvider>
  );
};
